import { notifyGamificationRewardOutcomes } from "@app/gamification/show-reward-outcome";
import {
  activeCommentAtom,
  CommentResponse,
  commentsAtom,
} from "@app/shared/atoms/comments-atom";
import type { GamificationRewardOutcome } from "@core/gamification";
import { parseError, URLS } from "@core/shared/utils";
import { errorToast, successToast } from "@design-system/components/toast";
import { useAction } from "@framework/shared/hooks";
import { trans } from "@mongez/localization";
import { FormSubmitOptions } from "@mongez/react-form";
import { useCallback } from "react";

type CommentMutationResponse = {
  rewardOutcome?: GamificationRewardOutcome | null;
};

export const useComments = (typeId: number, type: string) => {
  const commentsState = commentsAtom.useValue();
  const [addCommentAction, updateCommentAction] = [
    useAction<CommentMutationResponse>(URLS.comments.addComment),
    useAction<CommentMutationResponse>(URLS.comments.updateComment),
  ];

  const loadComments = useCallback(async () => {
    commentsAtom.merge({ state: "loading" });

    try {
      const response = await fetch(
        `${URLS.comments.root}?type=${type}&typeId=${typeId}`,
      );

      if (!response.ok) throw new Error("Unable to load comments");

      const data: CommentResponse = await response.json();

      if (!Array.isArray(data.comments)) {
        throw new Error("Invalid comments response");
      }

      commentsAtom.merge({
        comments: data.comments,
        paginationInfo: data.paginationInfo,
        totalComments:
          data.totalComments ??
          data.paginationInfo?.total ??
          data.comments.length,
        state: "loaded",
      });
    } catch (error) {
      commentsAtom.merge({ state: "error" });
      errorToast(trans("failedToLoadComments"));
    }
  }, [type, typeId]);

  const addComment = async ({ values, form }: FormSubmitOptions) => {
    const data = { type, typeId, ...values };
    const response = await addCommentAction(data);

    if (!response.error) {
      form.reset();

      successToast(trans("commentAddedSuccessfully"));
      notifyGamificationRewardOutcomes(response.data?.rewardOutcome);

      await loadComments();
    } else {
      errorToast(parseError(response));
    }
    form.submitting(false);
  };

  const updateComment = async ({ values, form }: FormSubmitOptions) => {
    const data = { type, typeId, ...values };
    const response = await updateCommentAction(data);

    if (!response.error) {
      form.reset();

      successToast(trans("commentUpdatedSuccessfully"));
      notifyGamificationRewardOutcomes(response.data?.rewardOutcome);

      activeCommentAtom.reset();

      await loadComments();
    } else {
      errorToast(parseError(response));
    }
    form.submitting(false);
  };

  return {
    addComment,
    commentsCount: commentsState.totalComments,
    commentsState,
    loadComments,
    updateComment,
  };
};
